import { z } from "zod";
import { SharedApiError, normalizeApiBaseUrl, type FetchLike } from "./shared";

export const WORKOS_USER_MANAGEMENT_BASE_URL =
  "https://api.workos.com/user_management";
export const userManagementAuthenticatePath = "/user_management/authenticate";
export const userManagementAuthorizePath = "/user_management/authorize";
export const userManagementLogoutPath = "/user_management/sessions/logout";

const userManagementUserSchema = z
  .object({
    id: z.string().nullish(),
    email: z.string().nullish(),
    first_name: z.string().nullish(),
    last_name: z.string().nullish(),
    profile_picture_url: z.string().nullish(),
  })
  .passthrough();

export const userManagementAuthCodeRequestSchema = z.object({
  code: z.string().min(1),
  client_id: z.string().min(1),
  redirect_uri: z.string().min(1),
  grant_type: z.literal("authorization_code"),
});

export const userManagementRefreshRequestSchema = z.object({
  client_id: z.string().min(1),
  grant_type: z.literal("refresh_token"),
  refresh_token: z.string().min(1),
});

export const userManagementAuthenticateRequestSchema = z.union([
  userManagementAuthCodeRequestSchema,
  userManagementRefreshRequestSchema,
]);

export const userManagementProxyRequestSchema = z.object({}).passthrough();

export const userManagementAuthenticateResponseSchema = z
  .object({
    access_token: z.string().nullish(),
    refresh_token: z.string().nullish(),
    user: userManagementUserSchema.nullish(),
    error: z.string().nullish(),
    message: z.string().nullish(),
  })
  .passthrough();

type UserManagementAuthenticateRequest = z.infer<
  typeof userManagementAuthenticateRequestSchema
>;

export type UserManagementAuthenticateResponse = z.infer<
  typeof userManagementAuthenticateResponseSchema
>;
export type UserManagementProxyRequest = z.infer<
  typeof userManagementProxyRequestSchema
>;

async function readJsonLikePayload(response: Response): Promise<unknown> {
  const rawText =
    typeof response.text === "function"
      ? await response.text().catch(() => "")
      : "";
  if (!rawText) return null;
  try {
    return JSON.parse(rawText);
  } catch {
    return rawText;
  }
}

export function buildUserManagementAuthorizeUrl({
  apiBaseUrl,
  clientId,
  redirectUri,
  responseType = "code",
  provider = "authkit",
}: {
  apiBaseUrl: string;
  clientId: string;
  redirectUri: string;
  responseType?: string;
  provider?: string;
}): string {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: responseType,
    provider,
  });
  return `${normalizeApiBaseUrl(apiBaseUrl)}${userManagementAuthorizePath}?${params.toString()}`;
}

export function buildUserManagementLogoutUrl({
  apiBaseUrl,
  clientId,
}: {
  apiBaseUrl: string;
  clientId: string;
}): string {
  const params = new URLSearchParams({ client_id: clientId });
  return `${normalizeApiBaseUrl(apiBaseUrl)}${userManagementLogoutPath}?${params.toString()}`;
}

function normalizeAuthenticatePayload(payload: unknown): unknown {
  const unwrapKeys = [
    "data",
    "result",
    "response",
    "authentication_response",
    "payload",
  ] as const;

  const parseMaybeJson = (value: unknown): unknown => {
    if (typeof value !== "string") return value;
    const trimmed = value.trim();
    if (trimmed.length === 0) return value;
    try {
      return JSON.parse(trimmed);
    } catch {
      return value;
    }
  };

  let normalized = parseMaybeJson(payload);
  if (Array.isArray(normalized) && normalized.length === 1) {
    normalized = parseMaybeJson(normalized[0]);
  }

  for (let depth = 0; depth < 4; depth++) {
    if (!normalized || typeof normalized !== "object") {
      break;
    }

    let unwrapped: unknown = null;
    for (const key of unwrapKeys) {
      if (key in normalized) {
        const value = (normalized as Record<string, unknown>)[key];
        if (value !== undefined && value !== null) {
          unwrapped = parseMaybeJson(value);
          break;
        }
      }
    }

    if (unwrapped === null) {
      break;
    }
    normalized = unwrapped;
  }

  return normalized;
}

function findAuthenticatePayloadCandidate(
  payload: unknown,
  maxDepth = 5,
): unknown {
  const seen = new Set<unknown>();

  const visit = (value: unknown, depth: number): unknown => {
    if (depth > maxDepth || value === null || value === undefined) {
      return null;
    }
    if (typeof value !== "object") {
      return null;
    }
    if (seen.has(value)) {
      return null;
    }
    seen.add(value);

    if (Array.isArray(value)) {
      for (const item of value) {
        const found = visit(item, depth + 1);
        if (found !== null) return found;
      }
      return null;
    }

    const record = value as Record<string, unknown>;
    if (
      typeof record.access_token === "string" ||
      typeof record.refresh_token === "string" ||
      typeof record.error === "string" ||
      typeof record.message === "string"
    ) {
      return record;
    }

    for (const child of Object.values(record)) {
      const parsedChild =
        typeof child === "string"
          ? (() => {
              const trimmed = child.trim();
              if (trimmed.length === 0) return child;
              try {
                return JSON.parse(trimmed);
              } catch {
                return child;
              }
            })()
          : child;
      const found = visit(parsedChild, depth + 1);
      if (found !== null) return found;
    }

    return null;
  };

  return visit(payload, 0);
}

async function parseUserManagementError(
  response: Response,
): Promise<SharedApiError> {
  const rawPayload = await readJsonLikePayload(response);
  const payload = normalizeAuthenticatePayload(rawPayload);
  const parsed = userManagementAuthenticateResponseSchema.safeParse(payload);
  if (parsed.success) {
    const message =
      parsed.data.message || parsed.data.error || "Authentication failed";
    return new SharedApiError(message, response.status, parsed.data);
  }
  return new SharedApiError("Authentication failed", response.status);
}

export async function authenticateWithUserManagement({
  apiBaseUrl,
  body,
  accessToken,
  fetchImpl,
}: {
  apiBaseUrl: string;
  body: UserManagementAuthenticateRequest;
  accessToken?: string;
  fetchImpl?: FetchLike;
}): Promise<UserManagementAuthenticateResponse> {
  const fetchFn = fetchImpl ?? fetch;
  const parsedBody = userManagementAuthenticateRequestSchema.parse(body);

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${userManagementAuthenticatePath}`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(parsedBody),
    },
  );

  if (!response.ok) {
    throw await parseUserManagementError(response);
  }

  const rawPayload = await readJsonLikePayload(response);
  const payload = normalizeAuthenticatePayload(rawPayload);
  const candidate = findAuthenticatePayloadCandidate(payload);
  const parsed = userManagementAuthenticateResponseSchema.safeParse(
    candidate ?? payload,
  );
  if (!parsed.success) {
    const payloadType =
      payload === null
        ? "null"
        : Array.isArray(payload)
          ? "array"
          : typeof payload;
    throw new SharedApiError(
      `Invalid authenticate response (${payloadType})`,
      500,
      {
        issues: parsed.error.issues,
        payload_type: payloadType,
        candidate_found: Boolean(candidate),
        payload_preview:
          typeof payload === "string" ? payload.slice(0, 200) : undefined,
      },
    );
  }

  return parsed.data;
}

export function buildWorkosAuthenticateUpstreamRequest({
  body,
  apiKey,
}: {
  body: unknown;
  apiKey?: string;
}): {
  url: string;
  init: RequestInit;
} {
  const parsedBody = userManagementProxyRequestSchema.parse(body);
  const requestHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (apiKey) {
    requestHeaders.Authorization = `Bearer ${apiKey}`;
  }

  return {
    url: `${WORKOS_USER_MANAGEMENT_BASE_URL}/authenticate`,
    init: {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify({
        ...parsedBody,
        ...(apiKey ? { client_secret: apiKey } : {}),
      }),
    },
  };
}

function buildWorkosProxyUrl(path: string, requestUrl: string): string {
  const query = requestUrl.split("?")[1] ?? "";
  const url = new URL(`${WORKOS_USER_MANAGEMENT_BASE_URL}${path}`);
  if (query.length > 0) {
    url.search = query;
  }
  return url.toString();
}

export function buildWorkosAuthorizeProxyRedirectUrl(
  requestUrl: string,
): string {
  return buildWorkosProxyUrl("/authorize", requestUrl);
}

export function buildWorkosLogoutProxyRedirectUrl(requestUrl: string): string {
  return buildWorkosProxyUrl("/sessions/logout", requestUrl);
}
